// Swiper

import { DataObjType } from '@customTypes/dataLayer';
import { hasDataLayer } from '@utils/dataLayer';

const DEBOUNCE_DELAY = 50;

export const Swiper = (swiperElement: HTMLDivElement) => {
  const container = swiperElement.querySelector<HTMLDivElement>('[data-swiper-container]');
  const list = swiperElement.querySelector<HTMLDivElement>('[data-swiper-list]');

  if (!container || !list) {
    throw new Error('Swiper container nebo swiper list nebyl nalezen.'); // Throw an error with a custom message in Czech
  }

  const navigation = swiperElement.querySelector<HTMLDivElement>('[data-swiper-navigation]');
  const items = [...swiperElement.querySelectorAll<HTMLElement>('[data-swiper-item]')];
  const navigationItems = navigation
    ? [...(navigation.querySelectorAll<HTMLElement>('[data-swiper-navigation-item]') || [])]
    : [];

  let currentIndex = 0;
  let dataScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;

  const handleSwiperItemGtm = () => {
    const evData = items[currentIndex]?.getAttribute('data-track-swiper-gtm');

    if (!evData || !hasDataLayer()) return;

    const evDataParsed = JSON.parse(evData) as DataObjType;

    void window.dataLayer.push(evDataParsed);
  };
  const setCurrentIndex = (index: number) => {
    const hasChangedIndex = currentIndex !== index;
    currentIndex = index;

    if (hasChangedIndex) handleSwiperItemGtm();
  };
  const getCurrentIndex = () => {
    return currentIndex;
  };

  const updateNavigation = () => {
    navigationItems.forEach((item, index) => {
      item.classList.toggle('is-active', index === currentIndex);
    });
  };

  const scrollToItem = (index: number) => {
    if (index >= 0 && index < items.length) {
      const item = items[index];
      const targetLeft = item.offsetLeft - list.offsetLeft;

      if ('scrollBehavior' in document.documentElement.style) {
        list.scrollLeft = targetLeft;
      } else {
        // Fallback for browsers that don't support smooth scrolling
        list.scrollTo({
          left: targetLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  const isAtEndOfScroll = () => {
    return list.scrollLeft + list.clientWidth >= list.scrollWidth;
  };

  const updateEndFadeClass = () => {
    container.classList.toggle('end-fade', !isAtEndOfScroll());
  };

  const updateSelectedItemOnScroll = () => {
    const listLeft = list.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - listLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (isAtEndOfScroll()) {
      setCurrentIndex(items.length - 1);
    } else {
      setCurrentIndex(closestIndex);
    }

    if (navigation) updateNavigation();
  };

  const handleScroll = () => {
    updateEndFadeClass();

    if (dataScrollTimeout !== null) {
      clearTimeout(dataScrollTimeout);
    }
    dataScrollTimeout = setTimeout(() => {
      updateSelectedItemOnScroll();
    }, DEBOUNCE_DELAY);
  };

  const handleTouchend = () => {
    if (isAtEndOfScroll()) {
      setCurrentIndex(items.length - 1);
      if (navigation) updateNavigation();
    }
  };

  const handleNavigationItemClick = (index: number) => {
    if (index !== -1) {
      setCurrentIndex(index);
      scrollToItem(index);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    isDragging = true;
    startX = e.pageX - list.offsetLeft;
    scrollLeft = list.scrollLeft;

    list.classList.add('is-mouse-drag');
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const x = e.pageX - list.offsetLeft;
    const walk = (x - startX) * 2;

    requestAnimationFrame(() => {
      list.scrollLeft = scrollLeft - walk;
    });
  };

  const handleMouseUp = () => {
    isDragging = false;

    list.classList.remove('is-mouse-drag');
  };

  list.addEventListener('scroll', handleScroll);
  list.addEventListener('touchend', handleTouchend);
  container.addEventListener('mousedown', handleMouseDown); // Use container instead of list
  container.addEventListener('mousemove', handleMouseMove); // Use container instead of list
  container.addEventListener('mouseup', handleMouseUp);

  updateEndFadeClass();

  if (navigation) {
    navigationItems.forEach((item, index) => {
      item.addEventListener('click', handleNavigationItemClick.bind(null, index));
    });

    updateNavigation();
  }

  return {
    scrollToItem,
    getCurrentIndex,
  };
};

export default Swiper;
